/**
 * MessagerieGroupesPanel — mini-groupes de la messagerie (item 8.4, mig 236).
 *
 * Trois vues enchaînées dans un seul panneau : la liste des groupes, les sujets
 * d'un groupe, puis le fil d'un sujet. Pas de routes dédiées — la messagerie
 * est un espace de lecture continue, et un aller-retour d'URL à chaque clic
 * casserait le fil de la conversation.
 *
 * Le garde-fou d'Henrico est visible à l'écran : un agent ne voit ni le bouton
 * « Nouveau groupe », ni la gestion des membres.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type MessagerieGroupe, type MessagerieGroupeDetail,
  type MessagerieSujet, type MessagerieFil, type CarnetEntry } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Card, Button, Badge, Alert, Field } from '@/components/ui';
import {
  Users, Plus, ArrowLeft, Loader2, AlertCircle, MessageSquare, Send, UserMinus,
} from 'lucide-react';

const INPUT_CLS =
  'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg';
const TEXTAREA_CLS =
  'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg';

function relatif(iso: string): string {
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  if (min < 1440) return `il y a ${Math.round(min / 60)} h`;
  // Jamais toISOString pour l'affichage : fuseau Europe/Paris explicite.
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(d);
}

type Vue = 'liste' | 'groupe' | 'fil';

export function MessagerieGroupesPanel() {
  const { user } = useAuth();
  const peutGerer = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  const [vue, setVue] = useState<Vue>('liste');
  const [groupes, setGroupes] = useState<MessagerieGroupe[]>([]);
  const [groupe, setGroupe] = useState<MessagerieGroupeDetail | null>(null);
  const [sujets, setSujets] = useState<MessagerieSujet[]>([]);
  const [fil, setFil] = useState<MessagerieFil | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Création de groupe
  const [formOuvert, setFormOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [carnet, setCarnet] = useState<CarnetEntry[]>([]);
  const [membreIds, setMembreIds] = useState<string[]>([]);

  // Nouveau sujet / réponse
  const [sujetObjet, setSujetObjet] = useState('');
  const [sujetCorps, setSujetCorps] = useState('');
  const [reponse, setReponse] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const chargerGroupes = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `tous` pour l'encadrement : un manager doit voir les groupes qu'il
      // administre même s'il n'en est pas membre.
      setGroupes((await api.messagerie.groupes.list(peutGerer)).groupes);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [peutGerer]);

  useEffect(() => { void chargerGroupes(); }, [chargerGroupes]);

  async function ouvrirGroupe(id: string) {
    setLoading(true); setError(null);
    try {
      const [g, s] = await Promise.all([
        api.messagerie.groupes.get(id),
        api.messagerie.groupes.sujets(id, { limit: 50 }),
      ]);
      setGroupe(g); setSujets(s.items); setVue('groupe');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Groupe illisible');
    } finally { setLoading(false); }
  }

  async function ouvrirFil(id: string) {
    setLoading(true); setError(null);
    try {
      setFil(await api.messagerie.sujets.fil(id));
      setVue('fil');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Fil illisible');
    } finally { setLoading(false); }
  }

  async function ouvrirFormulaire() {
    setFormOuvert(true);
    if (carnet.length === 0) {
      try { setCarnet((await api.messagerie.carnet()).items); }
      catch { setError("Carnet d'adresses indisponible"); }
    }
  }

  async function creerGroupe() {
    if (nom.trim().length < 2) return;
    setEnvoi(true); setError(null);
    try {
      await api.messagerie.groupes.creer({
        nom: nom.trim(),
        description: description.trim() || undefined,
        membreIds,
      });
      setFormOuvert(false); setNom(''); setDescription(''); setMembreIds([]);
      await chargerGroupes();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Création impossible');
    } finally { setEnvoi(false); }
  }

  async function posterSujet() {
    if (!groupe || !sujetObjet.trim() || !sujetCorps.trim()) return;
    setEnvoi(true); setError(null);
    try {
      await api.messagerie.groupes.poster(groupe.id, {
        objet: sujetObjet.trim(), corps: sujetCorps.trim(),
      });
      setSujetObjet(''); setSujetCorps('');
      await ouvrirGroupe(groupe.id);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Envoi impossible');
    } finally { setEnvoi(false); }
  }

  async function envoyerReponse() {
    if (!fil || !reponse.trim()) return;
    setEnvoi(true); setError(null);
    try {
      await api.messagerie.sujets.repondre(fil.messages[0]!.id, reponse.trim());
      setReponse('');
      await ouvrirFil(fil.messages[0]!.id);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Réponse impossible');
    } finally { setEnvoi(false); }
  }

  async function retirer(userId: string) {
    if (!groupe) return;
    try {
      await api.messagerie.groupes.retirerMembre(groupe.id, userId);
      await ouvrirGroupe(groupe.id);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Retrait impossible');
    }
  }

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* ===== Liste des groupes ===== */}
      {vue === 'liste' && (
        <>
          {peutGerer && !formOuvert && (
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={() => void ouvrirFormulaire()}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau groupe
              </Button>
            </div>
          )}

          {formOuvert && peutGerer && (
            <Card variant="glass" padded>
              <div className="space-y-3">
                <Field label="Nom du groupe *" htmlFor="grp-nom">
                  <input id="grp-nom" value={nom} onChange={(e) => setNom(e.target.value)}
                    maxLength={100} placeholder="Ex : Équipe voirie" className={INPUT_CLS} />
                </Field>
                <Field label="Description" htmlFor="grp-desc">
                  <input id="grp-desc" value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500} className={INPUT_CLS} />
                </Field>
                <fieldset>
                  <legend className="mb-2 text-sm font-medium text-ink">Membres</legend>
                  <div className="flex flex-wrap gap-1.5">
                    {carnet.map((c) => {
                      const actif = membreIds.includes(c.id);
                      return (
                        <button
                          key={c.id} type="button" aria-pressed={actif}
                          onClick={() => setMembreIds(actif
                            ? membreIds.filter((x) => x !== c.id)
                            : [...membreIds, c.id])}
                          className={[
                            'rounded-r-lg border px-2.5 py-1 text-xs transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc',
                            actif
                              ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                              : 'border-border bg-surface/40 text-ink-muted hover:border-grc/40',
                          ].join(' ')}
                        >
                          {c.nomComplet}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-2xs text-ink-muted">
                    Vous serez ajouté au groupe automatiquement.
                  </p>
                </fieldset>
                <div className="flex gap-2">
                  <Button variant="primary" size="sm" loading={envoi}
                    disabled={nom.trim().length < 2}
                    onClick={() => void creerGroupe()}>
                    Créer le groupe
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setFormOuvert(false)}>
                    Annuler
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {groupes.length === 0 ? (
            <Card variant="glass" padded>
              <div className="py-6 text-center">
                <Users className="mx-auto mb-2 h-6 w-6 text-ink-subtle" aria-hidden="true" />
                <p className="text-sm text-ink-muted">
                  {peutGerer
                    ? "Aucun groupe. Créez celui de vos équipes — voirie, périscolaire, astreinte."
                    : "Vous ne faites partie d'aucun groupe pour l'instant."}
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-2">
              {groupes.map((g) => (
                <button key={g.id} type="button" onClick={() => void ouvrirGroupe(g.id)}
                  className="block w-full text-left">
                  <Card variant="glass" padded className="transition-colors hover:border-grc/40">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium text-ink ${g.actif ? '' : 'line-through opacity-60'}`}>
                          {g.nom}
                        </p>
                        {g.description && (
                          <p className="mt-0.5 text-xs text-ink-muted line-clamp-1">{g.description}</p>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-1.5">
                        {!g.actif && <Badge variant="neutral" size="sm">Désactivé</Badge>}
                        {!g.estMembre && <Badge variant="warning" size="sm">Non membre</Badge>}
                        <Badge variant="info" size="sm">{g.membresCount}</Badge>
                      </div>
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== Sujets d'un groupe ===== */}
      {vue === 'groupe' && groupe && (
        <>
          <button type="button" onClick={() => { setVue('liste'); void chargerGroupes(); }}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-grc-fg">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Tous les groupes
          </button>

          <Card variant="glass" padded>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-ink">{groupe.nom}</h2>
                {groupe.description && (
                  <p className="mt-0.5 text-xs text-ink-muted">{groupe.description}</p>
                )}
              </div>
              <Badge variant="info" size="sm">{groupe.membresCount} membres</Badge>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {groupe.membres.map((m) => (
                <span key={m.userId}
                  className="inline-flex items-center gap-1 rounded-r-md border border-border bg-surface/40 px-2 py-0.5 text-2xs text-ink-muted">
                  {m.nomComplet}
                  {peutGerer && (
                    <button type="button" onClick={() => void retirer(m.userId)}
                      aria-label={`Retirer ${m.nomComplet}`}
                      className="ml-0.5 text-ink-subtle hover:text-urgent">
                      <UserMinus className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </Card>

          {/* Écrire est réservé aux membres — un manager non membre lit sans
              pouvoir s'inviter dans le fil sans prévenir. */}
          {groupe.estMembre && groupe.actif && (
            <Card variant="glass" padded>
              <div className="space-y-2">
                <input value={sujetObjet} onChange={(e) => setSujetObjet(e.target.value)}
                  maxLength={200} placeholder="Sujet…" className={INPUT_CLS} />
                <textarea value={sujetCorps} onChange={(e) => setSujetCorps(e.target.value)}
                  rows={3} maxLength={5000} placeholder="Votre message…" className={TEXTAREA_CLS} />
                <div className="flex justify-end">
                  <Button variant="primary" size="sm" loading={envoi}
                    disabled={!sujetObjet.trim() || !sujetCorps.trim()}
                    onClick={() => void posterSujet()}>
                    <Send className="h-4 w-4" aria-hidden="true" /> Lancer le sujet
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {sujets.length === 0 ? (
            <Card variant="glass" padded>
              <p className="py-4 text-center text-sm text-ink-muted">
                Aucun sujet dans ce groupe.
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {sujets.map((s) => (
                <button key={s.id} type="button" onClick={() => void ouvrirFil(s.id)}
                  className="block w-full text-left">
                  <Card variant="glass" padded className="transition-colors hover:border-grc/40">
                    <div className="flex items-start gap-2">
                      {!s.luParMoi && (
                        <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-info"
                          aria-label="Non lu" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm ${s.luParMoi ? 'font-medium' : 'font-semibold'} text-ink`}>
                          {s.objet}
                        </p>
                        <p className="mt-0.5 text-2xs text-ink-muted">
                          {s.auteurNom} · {relatif(s.dernierMessageAt)}
                          {s.reponsesCount > 0 && ` · ${s.reponsesCount} réponse${s.reponsesCount > 1 ? 's' : ''}`}
                        </p>
                      </div>
                      <MessageSquare className="h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== Fil ===== */}
      {vue === 'fil' && fil && (
        <>
          <button type="button" onClick={() => void ouvrirGroupe(fil.groupeId)}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-grc-fg">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> {fil.groupeNom}
          </button>

          <Card variant="glass" padded>
            <h2 className="text-base font-semibold text-ink">{fil.objet}</h2>
          </Card>

          <div className="space-y-2">
            {fil.messages.map((m) => (
              <Card key={m.id} variant="glass" padded>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-ink">{m.auteurNom}</span>
                  <span className="text-2xs text-ink-subtle">{relatif(m.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{m.corps}</p>
              </Card>
            ))}
          </div>

          <Card variant="glass" padded>
            <textarea value={reponse} onChange={(e) => setReponse(e.target.value)}
              rows={3} maxLength={5000} placeholder="Répondre dans ce fil…"
              className={TEXTAREA_CLS} />
            <div className="mt-2 flex justify-end">
              <Button variant="primary" size="sm" loading={envoi}
                disabled={!reponse.trim()} onClick={() => void envoyerReponse()}>
                <Send className="h-4 w-4" aria-hidden="true" /> Répondre
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
